import React, { useState, useCallback, useMemo } from 'react';
import BaseMap from './BaseMap';
import { HistoricalLayerSelector } from './historical-layers/HistoricalLayerSelector';
import { MissionHistoricalLayerSelector } from './historical-layers/MissionHistoricalLayerSelector';
import { HistoricalLayersService, HistoricalTileSource } from '../../types/historical-layers';
import { HistoricalLayersServiceImpl } from '../../services/historical-layers/HistoricalLayersService';
import { Coordinate } from 'ol/coordinate';
import { toLonLat } from 'ol/proj';
import Mission from '../../db/MissionClass';

/**
 * Enhanced BaseMap component with historical layers functionality
 * This component wraps BaseMap and adds historical satellite imagery selection
 * Supports both location-based (map center) and mission-based imagery fetching
 */

interface BaseMapProps {
  absoluteOverlays?: React.ReactNode[];
  trackingButton?: React.ReactNode;
  overlays?: React.ReactNode[];
  layers: React.ReactNode[];
  buttons: React.ReactNode[];
  statusItems?: React.ReactNode[];
  primaryActions?: React.ReactNode[];
  secondRowElements?: React.ReactNode[];
  enableSoilMap?: boolean;
  robotConnected?: boolean;
}

interface BaseMapWithHistoricalLayersProps extends BaseMapProps {
  /**
   * Optional historical layers service for dependency injection
   */
  historicalLayersService?: HistoricalLayersService;
  /**
   * Whether to show the historical timeline selector
   */
  showTimeline?: boolean;
  /**
   * Optional mission for mission-based imagery fetching
   * If provided, uses MissionHistoricalLayerSelector instead of location-based selector
   */
  mission?: Mission;
}

export const BaseMapWithHistoricalLayers: React.FC<BaseMapWithHistoricalLayersProps> = ({
  buttons = [],
  absoluteOverlays = [],
  historicalLayersService,
  showTimeline = false,
  mission,
  ...baseMapProps
}) => {
  // Use provided service or create default one (memoized to prevent recreation on each render)
  const service = useMemo(
    () => historicalLayersService || new HistoricalLayersServiceImpl(),
    [historicalLayersService],
  );

  // State for current tile source and selected year
  const [currentTileSource, setCurrentTileSource] = useState<HistoricalTileSource>(() => {
    const currentYear = service.getCurrentYear();

    return service.getTileSourceForYear(currentYear);
  });

  const [selectedYear, setSelectedYear] = useState<number>(() => service.getCurrentYear());

  // State for map center in lon/lat
  const [mapCenter, setMapCenter] = useState<[number, number] | undefined>(undefined);

  // Handle year change from the selector
  const handleYearChange = useCallback((year: number, tileSource: HistoricalTileSource) => {
    setSelectedYear(year);
    setCurrentTileSource(tileSource);
  }, []);

  // Handle map center changes from BaseMap
  const handleCenterChange = useCallback((center: Coordinate) => {
    // Convert from EPSG:3857 (Web Mercator) to EPSG:4326 (lon/lat)
    const [lon, lat] = toLonLat(center);

    setMapCenter([lon, lat]);
  }, []);

  // Create the historical layer selector as an overlay (positioned at bottom of map)
  // Use mission-based selector if mission is provided, otherwise use location-based selector
  const historicalLayerSelector = showTimeline ? (
    mission ? (
      <MissionHistoricalLayerSelector
        key="mission-historical-layer-selector"
        selectedYear={selectedYear}
        onYearChange={handleYearChange}
        historicalLayersService={service}
        mission={mission}
      />
    ) : (
      <HistoricalLayerSelector
        key="historical-layer-selector"
        selectedYear={selectedYear}
        onYearChange={handleYearChange}
        historicalLayersService={service}
        mapCenter={mapCenter}
      />
    )
  ) : null;

  // Add historical layer selector to absolute overlays only if showTimeline is true
  const enhancedOverlays = showTimeline ? [...absoluteOverlays, historicalLayerSelector] : absoluteOverlays;

  return (
    <BaseMap
      {...baseMapProps}
      buttons={buttons}
      absoluteOverlays={enhancedOverlays}
      onCenterChange={mission ? undefined : handleCenterChange} // Only track center for location-based mode
      externalTileSource={{
        url: currentTileSource.url,
        attribution: currentTileSource.attribution,
        sourceType: currentTileSource.sourceType,
        wmsParams: currentTileSource.wmsParams,
      }}
    />
  );
};
